"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { FAQ } from "@/lib/tools/types";

interface FAQAccordionProps {
  faqs: FAQ[];
}

/**
 * FAQ section with expandable items.
 * Click to expand/collapse with smooth animation.
 * Includes FAQ schema markup for SEO.
 */
export function FAQAccordion({ faqs }: FAQAccordionProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  return (
    <section className="space-y-4">
      <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-6">
        Frequently Asked Questions
      </h2>

      <div className="space-y-2">
        {faqs.map((faq, idx) => (
          <div
            key={idx}
            className="border border-border rounded-lg overflow-hidden transition-all hover:border-border/70"
          >
            <button
              onClick={() => setExpandedIndex(expandedIndex === idx ? null : idx)}
              className="w-full px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between hover:bg-secondary/50 transition-colors"
            >
              <h3 className="text-left text-sm sm:text-base font-semibold text-foreground">
                {faq.question}
              </h3>
              <ChevronDown
                className={`h-5 w-5 text-muted-foreground flex-shrink-0 ml-4 transition-transform ${
                  expandedIndex === idx ? "rotate-180" : ""
                }`}
              />
            </button>

            {/* Answer */}
            {expandedIndex === idx && (
              <div className="px-4 sm:px-5 py-4 border-t border-border bg-background/50">
                <div
                  className="text-sm sm:text-base text-foreground/90 leading-relaxed prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: faq.answer }}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* FAQ Schema for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer.replace(/<[^>]*>/g, ""), // Remove HTML tags for schema
              },
            })),
          }),
        }}
      />
    </section>
  );
}
